import { Component, computed, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import type { NestedStationData } from './org-chart.model';
import { OrgChartStore } from './org-chart.state';

/**
 * One station as a row inside the card of its OE.
 *
 * A station is a name and who answers for it, nothing more — nobody is
 * assigned to it — so it reads on one line at `STATION_ROW_HEIGHT` and never
 * opens. Slate, so it is not taken for a Stelle beside it. The edit and the
 * delete button show on hover or focus, over the row's right end, as on a
 * position row.
 */
@Component({
  selector: 'org-oe-station-row',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div
      class="group/station relative flex h-9 items-center gap-1.5 rounded-md border border-l-2 border-slate-300 border-l-slate-400 bg-slate-50 px-2"
      data-testid="oe-station-row"
    >
      <span class="min-w-0 truncate text-[13px] font-medium text-slate-800">{{
        station().name
      }}</span>
      @if (station().responsiblePersonName; as responsible) {
        <span
          class="min-w-0 truncate text-[11px] text-slate-500"
          [title]="responsible"
          >· {{ responsible }}</span
        >
      }

      @if (canEdit() || canDelete()) {
        <div
          class="absolute top-px right-1 flex h-8 items-center gap-0.5 bg-slate-50 pl-1 opacity-0 transition-opacity duration-150 group-hover/station:opacity-100 focus-within:opacity-100"
          data-testid="oe-station-row-actions"
        >
          @if (canEdit()) {
            <button
              type="button"
              class="flex items-center rounded-md p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              [title]="'organigram.node.editStation' | transloco"
              data-no-pan="true"
              data-no-drag="true"
              (click)="onEdit($event)"
            >
              <span class="material-icons text-base">edit</span>
            </button>
          }
          @if (canDelete()) {
            <button
              type="button"
              class="flex items-center rounded-md p-0.5 text-slate-400 hover:bg-destructive/10 hover:text-destructive"
              [title]="'organigram.node.deleteStation' | transloco"
              data-no-pan="true"
              data-no-drag="true"
              (click)="onDelete($event)"
            >
              <!-- Thickened to match the pencil; see the person row. -->
              <span class="material-icons text-base [-webkit-text-stroke:1.5px_currentColor]">close</span>
            </button>
          }
        </div>
      }
    </div>
  `,
  host: { class: 'block' },
})
export class OeStationRowComponent {
  readonly store = inject(OrgChartStore);
  readonly station = input.required<NestedStationData>();

  readonly canEdit = computed(() => this.station().canManage);
  readonly canDelete = computed(() => this.station().canDelete);

  onEdit(event: Event): void {
    if (!this.canEdit()) return;
    event.stopPropagation();
    this.store.edit({ kind: 'station', id: this.station().stationId });
  }

  onDelete(event: Event): void {
    if (!this.canDelete()) return;
    event.stopPropagation();
    this.store.remove({ kind: 'station', id: this.station().stationId });
  }
}
